import { Box, Typography, Stack, Alert, Autocomplete, TextField, CircularProgress } from "@mui/material";
import { useRecordContext, useGetOne, useGetList, useUpdate, useRefresh } from "react-admin";
import { ResourceName, Reimbursement, StoplossClaim } from "@/types";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { formatLongDate } from "@/utils/formatters/dateFormatter";
import { StatusChip } from "@/components/common/fields/StatusChipField";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { getStatusColor } from "@/utils/helpers/getStatusColor";
import { ColorType, ButtonStyleType, UIComponentSize } from "@/types/shared/uiComponents";
import { Link as RouterLink } from "react-router-dom";
import { InfoOutlined as InfoIcon, LinkOff as UnlinkedIcon, ArrowRightAlt as ArrowIcon } from "@mui/icons-material";
import Button from "@/components/ui/Button";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { useState } from "react";

const safeUSD = (n: unknown) => (typeof n === "number" && !Number.isNaN(n) ? formatUSD(n) : "—");

/** Simple label/value stat */
const Stat = ({ label, value }: { label: string; value: string }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={500}>
      {value}
    </Typography>
  </Box>
);

/**
 * Shows linked stop-loss claim context on the reimbursement show page.
 * If no claim is linked, shows a warning with an option to link one.
 */
export const LinkedClaimCard = () => {
  const record = useRecordContext<Reimbursement>();
  const { canEdit } = useResourcePermissions(ResourceName.REIMBURSEMENTS);
  const [isLinking, setIsLinking] = useState(false);
  const [update] = useUpdate();
  const refresh = useRefresh();

  const claimId = record?.claim_id_ref;

  // Fetch the linked claim
  const { data: claim, isLoading } = useGetOne<StoplossClaim>(
    ResourceName.STOPLOSS_CLAIMS,
    { id: claimId || "" },
    { enabled: !!claimId }
  );

  // Fetch available claims for linking (scoped to same claimant + client + policy)
  const claimFilter: Record<string, string> = {};
  if (record?.claimant_id_ref) claimFilter.claimant_id_ref = record.claimant_id_ref;
  if (record?.client_id_ref) claimFilter.client_id_ref = record.client_id_ref;
  if (record?.policy_id_ref) claimFilter.policy_id_ref = record.policy_id_ref;

  const { data: availableClaims = [], isLoading: isLoadingClaims } = useGetList<StoplossClaim>(
    ResourceName.STOPLOSS_CLAIMS,
    {
      filter: claimFilter,
      pagination: { page: 1, perPage: 100 },
      sort: { field: "created_datetime", order: "DESC" },
    },
    { enabled: isLinking && !!record?.claimant_id_ref }
  );

  const handleLink = async (claimToLink: StoplossClaim | null) => {
    if (!claimToLink || !record) return;
    await update(ResourceName.REIMBURSEMENTS, {
      id: record.id,
      data: { claim_id_ref: claimToLink.id },
      previousData: record,
    });
    setIsLinking(false);
    refresh();
  };

  // No claim linked — show warning with link action
  if (!claimId) {
    return (
      <Box>
        <Alert
          severity="warning"
          icon={<UnlinkedIcon fontSize="small" />}
          sx={{ mb: isLinking ? 2 : 0, fontSize: "0.8125rem" }}
          action={
            canEdit && !isLinking ? (
              <Button
                size={UIComponentSize.Small}
                variant={ButtonStyleType.Text}
                onClick={() => setIsLinking(true)}
                sx={{ whiteSpace: "nowrap" }}
              >
                Link Claim
              </Button>
            ) : undefined
          }
        >
          This reimbursement is not linked to a stop-loss claim.
        </Alert>

        {/* Claim selector */}
        {isLinking && (
          <Autocomplete
            options={availableClaims}
            loading={isLoadingClaims}
            getOptionLabel={(option) =>
              `${formatToTitleCase(option.stoploss_claim_type || "Claim")} · ${safeUSD(option.total_paid_amount)} · ${formatToTitleCase(option.claim_status)}`
            }
            onChange={(_, value) => handleLink(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select a claim to link"
                size="small"
                autoFocus
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingClaims ? <CircularProgress color="inherit" size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            size="small"
            noOptionsText={
              record?.claimant_id_ref ? "No claims found for this claimant" : "Set a claimant first to find claims"
            }
          />
        )}
      </Box>
    );
  }

  // Loading
  if (isLoading) {
    return (
      <Typography variant="body2" color="text.secondary">
        Loading claim...
      </Typography>
    );
  }

  // Claim not found
  if (!claim) {
    return (
      <Alert severity="info" icon={<InfoIcon fontSize="small" />} sx={{ fontSize: "0.8125rem" }}>
        Linked claim not found.
      </Alert>
    );
  }

  // Show claim summary
  return (
    <Box>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography variant="subtitle2" fontWeight={600}>
          {formatToTitleCase(claim.stoploss_claim_type || "Claim")}
        </Typography>
        <StatusChip
          label={formatToTitleCase(claim.claim_status)}
          color={getStatusColor(claim.claim_status) ?? ColorType.Default}
        />
      </Stack>

      {/* Stats */}
      <Box
        sx={{
          bgcolor: "action.hover",
          borderRadius: 1,
          px: 2,
          py: 1.5,
          mb: 2,
        }}
      >
        <Typography variant="body1" fontWeight={600} color="primary.main">
          {safeUSD(claim.total_paid_amount)}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="caption" color="text.secondary">
            Total Paid
          </Typography>
          {(claim as unknown as Record<string, unknown>).claim_items_count !== undefined && (
            <Typography variant="caption" color="text.disabled">
              · {String((claim as unknown as Record<string, unknown>).claim_items_count)} items
            </Typography>
          )}
        </Stack>
      </Box>

      <Stack spacing={0.5}>
        <Stack direction="row" spacing={3}>
          <Stat label="First Service" value={formatLongDate(claim.first_service_date)} />
          <Stat label="Last Service" value={formatLongDate(claim.last_service_date)} />
        </Stack>
        <Stack direction="row" spacing={3}>
          <Stat label="First Paid" value={formatLongDate(claim.first_paid_date)} />
          <Stat label="Last Paid" value={formatLongDate(claim.last_paid_date)} />
        </Stack>
        {claim.submitted_date && <Stat label="Submitted" value={formatLongDate(claim.submitted_date)} />}
      </Stack>

      {/* View Claim link */}
      <Button
        component={RouterLink}
        to={`/stoploss-claims/${claim.id}/show`}
        variant={ButtonStyleType.Outlined}
        size={UIComponentSize.Small}
        endIcon={<ArrowIcon sx={{ fontSize: 16 }} />}
        fullWidth
        sx={{ mt: 2 }}
      >
        View Claim Details
      </Button>
    </Box>
  );
};
